/**
 * Canonical form field metadata types.
 *
 * These types are the single source of truth for all form field metadata
 * across the Raven frontend. Every form - whether driven by backend
 * schema (registrations, claims, host/transaction templates) or defined
 * locally (login, settings, onboarding) - must express its fields using
 * this contract.
 *
 * ARCHITECTURAL DECISIONS:
 *
 * 1. Required semantics preserve the backend's three-level model:
 *    `required_to_save`, `required_to_publish`, and generic `required`.
 *    The normalizers set these from backend shapes; the validation system
 *    reads them based on the current submission mode.
 *
 * 2. Review formatting metadata lives on the field itself (via
 *    `review_format`) so that review screens never need raw backend
 *    fields to render labels or resolve option values.
 *
 * 3. Visibility and conditional display are first-class so wizard steps
 *    can express field filtering at the contract level.
 *
 * 4. The contract is intentionally rich enough that pages never need to
 *    reconstruct metadata that this system should own.
 */

/** All field types the backend can send. */
export type FieldType =
  | 'TEXT'
  | 'RICH_TEXT'
  | 'LONG_TEXT'
  | 'TEXTAREA'
  | 'NUMBER'
  | 'DECIMAL'
  | 'BOOLEAN'
  | 'SELECT'
  | 'DROPDOWN'
  | 'MULTI_SELECT'
  // A real backend field type (DynamicFieldType.DOUBLE_SELECT) that was
  // missing here, so any metadata carrying it could not be passed to
  // defineField / formatDisplayValue without a cast.
  | 'DOUBLE_SELECT'
  | 'DATE'
  | 'DATE_TIME'
  | 'DOCUMENT'
  | 'FILE'
  | 'IMAGE'
  | 'VIDEO'
  | 'LINK'
  | 'MONEY'
  | 'CURRENCY'
  | 'ADDRESS'
  | 'ENTITY_SEARCH'
  | 'PHONE'
  | 'EMAIL'

/**
 * Per-option severity used by checklist-style MULTI_SELECT fields to flag
 * findings (e.g. an inspection-report section where a chosen option is a
 * red/amber issue). Optional and additive: standard forms never set it, so
 * existing fields are unaffected. The renderer shows a small chip after the
 * label for 'red'/'yellow'; 'none' (or absence) renders no chip.
 */
export type OptionSeverity = 'red' | 'yellow' | 'none'

export interface SelectOption {
  value: string
  label: string
  /** Optional finding severity for checklist-style options. */
  severity?: OptionSeverity
  /** Optional heading the option is listed under (see SelectOptionShape.group). */
  group?: string
}

/**
 * A rule exactly as stored in an attribute's
 * `configuration_data.validation_rules` and served verbatim by
 * `FieldMetaSerializer`: lower-case `validation_type`, bound under a key
 * named after it. There is no generic `value` key.
 */
export type ValidationRule =
  | { validation_type: 'pattern'; pattern: string; failure_message: string }
  | { validation_type: 'regex'; regex: string; failure_message: string }
  | {
      validation_type: 'serial_range'
      ranges: { from: string; to: string }[]
      failure_message: string
    }
  | { validation_type: 'min_length'; min_length: number; failure_message: string }
  | { validation_type: 'max_length'; max_length: number; failure_message: string }
  | { validation_type: 'min_value'; min_value: number | string; failure_message: string }
  | { validation_type: 'max_value'; max_value: number | string; failure_message: string }
  | { validation_type: 'future'; failure_message: string }
  | { validation_type: 'past'; failure_message: string }

/**
 * Submission mode determines which required semantics apply.
 *
 * - 'save': only `required_to_save` fields are enforced
 * - 'publish': `required_to_save` + `required_to_publish` fields enforced
 * - 'submit': all `required` fields enforced (claims, consumer forms)
 */
export type SubmissionMode = 'save' | 'publish' | 'submit'

/**
 * How a field should be formatted in review/summary screens.
 */
export type ReviewFormat =
  | 'text'
  | 'boolean'
  | 'date'
  | 'datetime'
  | 'currency'
  | 'number'
  | 'select'
  | 'multi_select'
  | 'file'
  | 'file_count'
  | 'address'
  | 'phone'
  | 'email'
  | 'entity_search'

/**
 * Canonical field metadata shape that every form field must satisfy.
 *
 * Backend endpoints (registration-metadata, claim-metadata, host/transaction
 * templates) all return slightly different shapes. Callers should normalise
 * into this via the adapter functions in `./normalise.ts`.
 *
 * Local forms (login, settings) can construct these directly.
 */
export interface FormFieldMeta {
  /** Unique identifier - backend `code_name`, UUID, or a local key. */
  code_name: string

  /** Human-readable label shown next to the field. */
  display_name: string

  /** Field type determines which input component is rendered. */
  field_type: FieldType

  // ── Required semantics ────────────────────────────────────
  /**
   * Generic required flag. For forms with a single submission mode
   * (e.g. claims, consumer registration), this is the only flag needed.
   */
  required: boolean

  /**
   * Required to save as draft. When set, the field must be filled even
   * for draft saves. Only relevant for forms that distinguish save/publish.
   */
  required_to_save?: boolean

  /**
   * Required to publish / finalize. Stricter than `required_to_save`.
   * Only relevant for forms that distinguish save/publish.
   */
  required_to_publish?: boolean

  // ── Display metadata ──────────────────────────────────────
  /** Hint text shown as placeholder or below the field. */
  hint?: string

  /** Longer description text shown below the field. */
  description?: string

  /** Options for SELECT / MULTI_SELECT / DROPDOWN fields. */
  options?: SelectOption[]

  // ── Validation ────────────────────────────────────────────
  /** Backend validation rules applied client-side. */
  validation_rules?: ValidationRule[]

  // ── Ordering and layout ───────────────────────────────────
  /** Sort order within the form. */
  order?: number

  /**
   * Logical group for layout purposes (e.g. 'purchase', 'customer',
   * 'host_attributes'). Rendering code can group fields by this key.
   */
  group?: string

  // ── Field state ───────────────────────────────────────────
  /** Whether the field is disabled (read-only). */
  disabled?: boolean

  /** Whether the field is completely hidden from the form. */
  hidden?: boolean

  // ── File upload ───────────────────────────────────────────
  /**
   * Use case for file uploads (e.g. 'CLAIM_EVIDENCE', 'PRODUCT_IMAGE').
   * Determines the upload endpoint and file handling strategy.
   */
  file_use_case?: string

  /**
   * Whether multiple files can be uploaded for this field.
   * Defaults to false (single file).
   */
  file_multiple?: boolean

  // ── Review / display formatting ───────────────────────────
  /**
   * How this field should be formatted in review/summary screens.
   * When omitted, inferred from `field_type`.
   */
  review_format?: ReviewFormat

  // ── Raw backend data ──────────────────────────────────────
  /**
   * Arbitrary extra configuration from the backend.
   * Should not be needed for standard rendering - if it is, extend
   * the canonical contract instead.
   */
  configuration_data?: Record<string, unknown>

  /** Backend UUID if the field comes from a backend schema. */
  source_uuid?: string

  /**
   * Attribute level from the backend (e.g. 'HOLDING', 'HOST').
   * Used for filtering fields across different form sections.
   */
  attribute_level?: string

  /**
   * Backend column this field maps to (e.g. for host templates
   * that alias standard columns). Used for deduplication.
   */
  maps_to_column?: string

  /** Attribute visibility from backend schema. */
  visibility?: string
}

/** Field types that represent file uploads. */
export const FILE_FIELD_TYPES = new Set<string>([
  'FILE',
  'DOCUMENT',
  'IMAGE',
  'VIDEO',
])

/** Field types that are textual / long-form. */
export const TEXTAREA_FIELD_TYPES = new Set<string>([
  'TEXTAREA',
  'LONG_TEXT',
  'RICH_TEXT',
])

/** Field types that represent numeric values. */
export const NUMERIC_FIELD_TYPES = new Set<string>([
  'NUMBER',
  'MONEY',
  'DECIMAL',
  'CURRENCY',
])

/**
 * Check if a field is required for a given submission mode.
 *
 * This is the single authority for required-field gating. All validation
 * schemas, required markers, and button-disabled logic must use this.
 */
export function isFieldRequired(
  field: FormFieldMeta,
  mode: SubmissionMode = 'submit',
): boolean {
  switch (mode) {
    case 'save':
      return field.required_to_save ?? false
    case 'publish':
      return field.required_to_publish ?? field.required_to_save ?? field.required
    case 'submit':
      return field.required
  }
}

/**
 * Infer the review format from a field's type when not explicitly set.
 */
export function inferReviewFormat(field: FormFieldMeta): ReviewFormat {
  if (field.review_format) return field.review_format

  const ft = field.field_type.toUpperCase()

  if (ft === 'BOOLEAN') return 'boolean'
  if (ft === 'DATE') return 'date'
  if (ft === 'DATE_TIME') return 'datetime'
  if (ft === 'MONEY' || ft === 'CURRENCY') return 'currency'
  if (ft === 'NUMBER' || ft === 'DECIMAL') return 'number'
  if (ft === 'SELECT' || ft === 'DROPDOWN') return 'select'
  if (ft === 'MULTI_SELECT') return 'multi_select'
  if (FILE_FIELD_TYPES.has(ft)) return 'file'
  if (ft === 'ADDRESS') return 'address'
  if (ft === 'PHONE') return 'phone'
  if (ft === 'EMAIL') return 'email'
  if (ft === 'ENTITY_SEARCH') return 'entity_search'

  return 'text'
}

/**
 * Define a local form field (not from backend schema).
 *
 * Convenience function that provides sensible defaults so local forms
 * don't need to specify every field property.
 */
export function defineField(
  partial: Pick<FormFieldMeta, 'code_name' | 'display_name' | 'field_type'> &
    Partial<FormFieldMeta>,
): FormFieldMeta {
  return {
    required: false,
    order: 0,
    ...partial,
  }
}
